import { giftCardsApiSlice, useLazyValidateGiftCardQuery } from '../../../store/api-slices';
import { batch, useDispatch, useSelector } from 'react-redux';
import { GiftCard } from '../../../services/exports/Interfaces';
import { selectCompanyId } from '../../../store/slices/InitialDataSlice';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import giftCardSlice, { selectGiftCard } from '../../../store/slices/GiftCardSlice';
import { selectCachedOrder } from '../../../store/slices/OrderSlice';
import discountDisplacementSlice from '../../../store/slices/DiscountDisplacementSlice';
import { actionCreators } from '../../../store/actions';
import { COUPON_TYPES } from '../../../services/exports/Constants';

const useGiftCard = () => {
  const giftCard = useSelector(selectGiftCard);
  const companyId = useSelector(selectCompanyId);
  const cachedOrder = useSelector(selectCachedOrder);
  const [validate, result] = useLazyValidateGiftCardQuery();
  const dispatch = useDispatch();
  const { t } = useTranslation(null, { keyPrefix: 'Features:GiftCards:useGiftCard' });

  // A gift card is exclusive with everything: applying one clears an active points redemption
  // (the offer is already cleared by the offer watchers). Record what it displaced so the notice
  // can explain it and Undo can restore the points (FOO-2555 / PROD-407).
  const displacePointsForGiftCard = (): void => {
    const points = cachedOrder?.coupon_type === COUPON_TYPES.CASHBACK ? (cachedOrder?.cashback_points ?? 0) : 0;
    if (points <= 0) {
      return;
    }

    dispatch(discountDisplacementSlice.actions.setCouponDisplacedPoints({ kind: 'gift', label: '', points }));
    dispatch(actionCreators.order.updateOrder({ coupon_type: null, coupon_details: null, cashback_points: null }));
  };

  const apply = async (code: string): Promise<boolean> => {
    return validate(
      {
        code,
        companyId: companyId,
      },
      true,
    )
      .unwrap()
      .then((data) => {
        displacePointsForGiftCard();
        setGiftCard(data);

        toast.success(t('gift_card_applied'));

        return true;
      })
      .catch(() => {
        _remove();

        toast.error(t('invalid_gift_card'));

        return false;
      });
  };

  const _remove = () => batch(() => [setGiftCard(), invalidate()]);
  const remove = () => {
    _remove();
    toast.error(t('gift_card_removed'));
  };

  const invalidate = () => dispatch(giftCardsApiSlice.util.resetApiState());
  const setGiftCard = (data?: GiftCard) => dispatch(giftCardSlice.actions.apply(data));

  return { apply, invalidate, isLoading: result.isLoading, giftCard, remove };
};

export default useGiftCard;
